/**
 * Import von Creatorn und Marken aus einer Tabelle (CSV aus Excel, Numbers, Google Tabellen, Notion): lesen, Spalten erkennen,
 * je Zeile prüfen und Dubletten markieren. Reine Funktionen ohne Datenbank; die Seite legt die geprüften Zeilen an.
 */
import { csvCellGuard, csvFormulaUnguard, isValidIban, normalizeCountry } from "@talentkontor/domain";
import {
  cleanHandle, cleanPhone, cleanText, CONTACT_ROLES, emailValid, HANDLE_RULE, percentToBp, PHONE_RULE, splitName,
  type BrandFull, type BrandPatch, type ContactRow, type PayoutRow, type TalentFull, type TalentPatch,
} from "./model";
import { t } from "../../../lib/sprache";

/** CSV lesen: Trenner Semikolon, Komma oder Tabulator (aus der ersten Zeile erkannt), Anführungszeichen wie Excel, BOM weg. */
export function parseCsv(text: string): string[][] {
  // Zellen aus eigenen Exporten tragen vor = + - @ ein Schutz-Apostroph (csvCellGuard); beim Einlesen fällt es weg
  const push = (r: string[], c: string) => r.push(csvFormulaUnguard(c));
  const src = text.replace(/^\uFEFF/, "");
  const firstLine = src.split(/\r?\n/, 1)[0] ?? "";
  const count = (c: string) => firstLine.split(c).length - 1;
  const sep = [";", "\t", ","].sort((a, b) => count(b) - count(a))[0]!;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === "") quoted = true;
    else if (ch === sep) { push(row, cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      push(row, cell);
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  push(row, cell);
  if (row.some((c) => c.trim() !== "")) rows.push(row);
  return rows;
}

/** Vergleichsform von Spaltennamen und Werten: klein, Umlaute ausgeschrieben, nur Buchstaben und Ziffern. */
export const fold = (s: string) => s.trim().toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss").replace(/[^a-z0-9]/g, "");

export type ImportKind = "creator" | "marken";
/** Übernahme des Bestands (0064): laufende Kooperationen und offene Rechnungen aus dem bisherigen System */
export type BestandKind = "kooperationen" | "rechnungen";

/** Erkannte Spaltennamen (klein, ohne Umlaute, Leerzeichen und Zeichen). */
const CREATOR_COLUMNS: Record<string, string[]> = {
  name: ["name", "vollstaendigername", "creator", "talent"],
  first_name: ["vorname", "firstname", "first"],
  last_name: ["nachname", "lastname", "familienname", "last"],
  display_name: ["anzeigename", "displayname", "kuenstlername"],
  email: ["email", "mail", "emailadresse"],
  instagram: ["instagram", "insta", "ig"],
  tiktok: ["tiktok"],
  youtube: ["youtube", "yt"],
  whatsapp: ["whatsapp", "telefon", "handy", "mobil", "phone"],
  street: ["strasse", "adresse", "street", "strasseundhausnummer"],
  postal_code: ["plz", "postleitzahl", "zip", "postalcode"],
  city: ["ort", "stadt", "city"],
  country: ["land", "country"],
  vat_id: ["ustidnr", "ustid", "uid", "uidnr", "uidnummer", "vatid", "umsatzsteueridentifikationsnummer", "mwstnr", "mwstnummer", "uidmwstnr"],
  tax_number: ["steuernummer", "taxnumber", "stnr"],
  // PRAXIS-R2-8: Rechtsform (KSK), Geburtsdatum und Erziehungsberechtigte (Minderjährige), Gutschriftverfahren
  legal_form: ["rechtsform", "legalform", "unternehmensform"],
  birth_date: ["geburtsdatum", "geboren", "birthdate", "dateofbirth", "geburtstag"],
  guardian_name: ["erziehungsberechtigte", "erziehungsberechtigter", "erziehungsberechtigt", "elternteil", "eltern", "guardian", "guardianname"],
  guardian_email: ["erziehungsberechtigteemail", "erziehungsberechtigteremail", "emailerziehungsberechtigte", "emailerziehungsberechtigter",
    "emaildererziehungsberechtigten", "elternemail", "elternmail", "emaileltern", "guardianemail"],
  self_billing_agreed_on: ["gutschriftverfahren", "gutschriftverfahrenvereinbartam", "gutschriftverfahrenvereinbart", "gutschriftverfahrenseit", "gutschriftvereinbart", "gutschriftvereinbartam", "gutschriftseit", "selfbilling", "selfbillingagreedon"],
  commission: ["provision", "provisionprozent", "agenturanteil", "commission"],
  company_name: ["firma", "firmenname", "company"],
  small_business: ["kleinunternehmer", "kleinunternehmerin", "kleinunternehmerregelung", "kleinunternehmer19ustg", "kleinunternehmer19", "smallbusiness"],
  language: ["sprache", "language", "mailsprache"],
  iban: ["iban"],
  bank_account_holder: ["kontoinhaber", "kontoinhaberin", "inhaber", "accountholder"],
  bic: ["bic", "swift", "bicswift"],
  bank_name: ["bank", "bankname", "kreditinstitut"],
};
const BRAND_COLUMNS: Record<string, string[]> = {
  name: ["name", "marke", "anzeigename", "brand"],
  legal_name: ["rechtlichername", "firmenname", "firma", "legalname", "rechtlicherfirmenname"],
  street: ["strasse", "adresse", "street", "strasseundhausnummer"],
  postal_code: ["plz", "postleitzahl", "zip", "postalcode"],
  city: ["ort", "stadt", "city"],
  country: ["land", "country"],
  vat_id: ["ustidnr", "ustid", "uid", "vatid"],
  invoice_email: ["rechnungsemail", "rechnungsmail", "email", "mail", "invoiceemail"],
  payment_terms_days: ["zahlungsziel", "zahlungszieltage", "paymentterms"],
  register_info: ["handelsregister", "register", "firmenbuch"],
  language: ["sprache", "language", "belegsprache"],
  invoice_route: ["abrechnungsweg", "abrechnung", "invoiceroute"],
  contact_name: ["ansprechpartner", "ansprechpartnerin", "ansprechperson", "kontakt", "kontaktperson", "contact", "contactname"],
  contact_email: ["kontaktemail", "ansprechpartneremail", "kontaktmail", "contactemail"],
  contact_phone: ["kontakttelefon", "telefon", "phone"],
  contact_role: ["rolle", "kontaktrolle", "zustaendigkeit", "zustaendig", "role"],
};

export function columnMap(kind: ImportKind, header: readonly string[]): Record<string, number> {
  const known = kind === "creator" ? CREATOR_COLUMNS : BRAND_COLUMNS;
  const out: Record<string, number> = {};
  header.forEach((h, i) => {
    const f = fold(h);
    for (const [key, aliases] of Object.entries(known)) if (!(key in out) && aliases.includes(f)) out[key] = i;
  });
  return out;
}

export const CREATOR_TEMPLATE = "Vorname;Nachname;E-Mail;Instagram;TikTok;Straße;PLZ;Ort;Land;Steuernummer;USt-IdNr.;Kleinunternehmer;Sprache;Provision;IBAN;Kontoinhaber\r\nMara;Linden;mara.linden@example.com;mara.linden;;Lindenallee 4;20257;Hamburg;DE;;;nein;de;30;;\r\n";
export const BRAND_TEMPLATE = "Name;Rechtlicher Name;Straße;PLZ;Ort;Land;USt-IdNr.;Rechnungs-E-Mail;Zahlungsziel;Sprache;Abrechnungsweg;Ansprechpartner;Kontakt-E-Mail;Rolle\r\nLunava;Lunava GmbH;Beispielstraße 1;10115;Berlin;DE;DE000000000;rechnung@example.com;14;de;über die Agentur;Studio-Team;studio@example.com;Rechnung, Vertrag\r\n";

/** Zusätze einer Zeile, die nach dem Anlegen gespeichert werden: Bankdaten und Gutschriftverfahren des Creators, Ansprechpartner der Marke */
export interface ImportExtra {
  payout?: Pick<PayoutRow, "iban" | "bic" | "bank_account_holder" | "bank_name">;
  /** Gutschriftverfahren vereinbart am (JJJJ-MM-TT); nur der Admin trägt es ein (wie im Profil) */
  selfBillingAgreedOn?: string;
  contact?: Omit<ContactRow, "id" | "org_id" | "brand_id" | "created_at">;
}
export interface ImportRow<P> {
  line: number;
  values: P | null;
  errors: string[];
  /** gleiche E-Mail oder gleicher Name schon in der Agentur bzw. weiter oben in der Datei */
  duplicate: string | null;
  label: string;
  extra?: ImportExtra;
  /** Hinweise ohne Fehler (z. B. Kontoinhaber aus dem Namen übernommen) */
  notes?: string[];
}

/** ja/nein aus Tabellen: ja, x, 1, wahr, true, yes; nein, 0, falsch, false, no, leer. Unbekannt: undefined. */
export function yesNo(v: string | null): boolean | null | undefined {
  if (v === null) return null;
  const f = fold(v);
  if (f === "") return null;
  if (["ja", "x", "1", "wahr", "true", "yes", "y", "j"].includes(f)) return true;
  if (["nein", "0", "falsch", "false", "no", "n", "-"].includes(f)) return false;
  return undefined;
}
function languageOf(v: string | null, errors: string[]): "de" | "en" | null {
  if (v === null) return null;
  const f = fold(v);
  if (["de", "deutsch", "german", "ger"].includes(f)) return "de";
  if (["en", "englisch", "english", "eng"].includes(f)) return "en";
  errors.push(t(`Sprache „${v}“: de oder en`, `Language “${v}”: de or en`));
  return null;
}
export function routeOf(v: string | null, errors: string[]): "via_agency" | "direct_to_brand" | null {
  if (v === null) return null;
  const f = fold(v);
  if (["agentur", "ueberdieagentur", "uberdieagentur", "viaagency", "viaagentur", "ueberagentur"].includes(f)) return "via_agency";
  if (["direkt", "direktandiemarke", "creator", "creatordirekt", "creatordirektandiemarke", "directtobrand", "direkt an die marke"].includes(f) || f.startsWith("direkt")) return "direct_to_brand";
  if (["standard", "agenturstandard"].includes(f)) return null;
  errors.push(t(`Abrechnungsweg „${v}“: über die Agentur oder direkt an die Marke`, `Invoicing route “${v}”: über die Agentur or direkt an die Marke`));
  return null;
}
const ROLE_ALIASES: Record<string, string> = {
  rechnung: "invoice", rechnungen: "invoice", invoice: "invoice", rechnungsempfaenger: "invoice",
  rechnunginkopie: "invoice_cc", kopie: "invoice_cc", cc: "invoice_cc", vertrag: "contract", vertraege: "contract", contract: "contract",
  briefing: "briefing", insights: "insights", freigabe: "approval", approval: "approval",
};
function rolesOf(v: string | null, errors: string[]): string[] {
  if (v === null) return [];
  const out: string[] = [];
  for (const part of v.split(/[,/;+|]| und /)) {
    const f = fold(part);
    if (!f) continue;
    const r = ROLE_ALIASES[f];
    if (!r) errors.push(t(`Rolle „${part.trim()}“ unbekannt (${CONTACT_ROLES.map((x) => x.label).join(", ")})`, `Role “${part.trim()}” unknown (${CONTACT_ROLES.map((x) => x.label).join(", ")})`));
    else if (!out.includes(r)) out.push(r);
  }
  return out;
}

/** Zweistellige Jahreszahl: bis zum laufenden Jahr 20xx, danach 19xx (Geburtsdaten „12.05.95“) */
const twoDigitYear = (yy: number) => (2000 + yy <= new Date().getUTCFullYear() ? 2000 + yy : 1900 + yy);
/** Datum aus der Tabelle: 24.12.2008, 24.12.08, 2008-12-24. Leer = null, ungültig = undefined. */
export function importDay(raw: string | null): string | null | undefined {
  const s = (raw ?? "").trim();
  if (!s) return null;
  let y: number, m: number, d: number;
  let x = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (x) [y, m, d] = [Number(x[1]), Number(x[2]), Number(x[3])];
  else if ((x = /^(\d{1,2})[./](\d{1,2})[./](\d{2}|\d{4})$/.exec(s))) [d, m, y] = [Number(x[1]), Number(x[2]), x[3]!.length === 2 ? twoDigitYear(Number(x[3])) : Number(x[3])];
  else return undefined;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d || y < 1900) return undefined;
  return dt.toISOString().slice(0, 10);
}

const countryOf = (raw: string | null, errors: string[]): string | null => {
  if (!raw) return null;
  const c = normalizeCountry(raw);
  if (!c) errors.push(t(`Land „${raw}“ unbekannt`, `Country “${raw}” unknown`));
  return c;
};

export function creatorRows(rows: readonly string[][], existing: readonly Pick<TalentFull, "email" | "first_name" | "last_name">[],
  opts: { bank: boolean; selfBilling?: boolean } = { bank: true }): { map: Record<string, number>; rows: ImportRow<TalentPatch>[] } {
  const [header = [], ...body] = rows;
  const map = columnMap("creator", header);
  // Ohne Recht auf Bankdaten gelten die Spalten als nicht erkannt (die Seite nennt sie ausgelassen); ebenso das Gutschriftverfahren
  if (!opts.bank) for (const k of ["iban", "bank_account_holder", "bic", "bank_name"]) delete map[k];
  if (!(opts.selfBilling ?? opts.bank)) delete map.self_billing_agreed_on;
  const seenMail = new Set(existing.map((t) => (t.email ?? "").toLowerCase()).filter(Boolean));
  const seenName = new Set(existing.map((t) => `${t.first_name} ${t.last_name ?? ""}`.trim().toLowerCase()));
  const out = body.map((r, i): ImportRow<TalentPatch> => {
    const get = (k: string) => (map[k] === undefined ? null : cleanText(r[map[k]!]));
    const errors: string[] = [];
    let first = get("first_name");
    let last = get("last_name");
    if (!first && get("name")) ({ first, last } = splitName(get("name")!));
    if (!first) errors.push(t("Vorname fehlt", "First name missing"));
    const email = get("email")?.toLowerCase() ?? null;
    if (email && !emailValid(email)) errors.push(t("E-Mail ungültig", "Email invalid"));
    const ig = cleanHandle(get("instagram")), tt = cleanHandle(get("tiktok")), yt = cleanHandle(get("youtube"));
    if (ig && !HANDLE_RULE.instagram.test(ig)) errors.push(t("Instagram ungültig", "Instagram invalid"));
    if (tt && !HANDLE_RULE.tiktok.test(tt)) errors.push(t("TikTok ungültig", "TikTok invalid"));
    if (yt && !HANDLE_RULE.youtube.test(yt)) errors.push(t("YouTube ungültig", "YouTube invalid"));
    const phone = cleanPhone(get("whatsapp"));
    const bp = percentToBp((get("commission") ?? "").replace("%", ""));
    if (bp === undefined) errors.push(t("Provision ungültig", "Commission invalid"));
    const country = countryOf(get("country"), errors);
    const small = yesNo(get("small_business"));
    if (small === undefined) errors.push(t("Kleinunternehmer: ja oder nein", "Kleinunternehmer: ja or nein"));
    const language = languageOf(get("language"), errors);
    const name = `${first ?? ""} ${last ?? ""}`.trim();
    // PRAXIS-R2-8: Geburtsdatum, Erziehungsberechtigte, Gutschriftverfahren (Datum der Vereinbarung)
    const birth = importDay(get("birth_date"));
    if (birth === undefined) errors.push(t(`Geburtsdatum „${get("birth_date")}“: bitte als TT.MM.JJJJ`, `Date of birth “${get("birth_date")}”: please as DD.MM.YYYY`));
    const guardianEmail = get("guardian_email")?.toLowerCase() ?? null;
    if (guardianEmail && !emailValid(guardianEmail)) errors.push(t("E-Mail der Erziehungsberechtigten ungültig", "Parent or guardian email invalid"));
    const sbRaw = get("self_billing_agreed_on");
    const sbDay = importDay(sbRaw);
    if (sbRaw && (sbDay === undefined || sbDay === null)) errors.push(t(`Gutschriftverfahren „${sbRaw}“: bitte das Datum der Vereinbarung (TT.MM.JJJJ)`, `Self-billing “${sbRaw}”: please the date of the agreement (DD.MM.YYYY)`));
    // Bankdaten: nur mit Recht (die Seite blendet die Spalten sonst aus), IBAN mit Prüfziffer
    const notes: string[] = [];
    let payout: ImportExtra["payout"];
    const ibanRaw = opts.bank ? get("iban")?.replace(/\s+/g, "").toUpperCase() ?? null : null;
    if (ibanRaw) {
      if (!isValidIban(ibanRaw)) errors.push(t("IBAN ungültig (Prüfziffer)", "IBAN invalid (check digit)"));
      const holder = get("bank_account_holder") ?? (get("company_name") || name || null);
      if (!get("bank_account_holder")) notes.push(t("Kontoinhaber aus dem Namen übernommen", "Account holder taken from the name"));
      payout = { iban: ibanRaw.replace(/(.{4})/g, "$1 ").trim(), bank_account_holder: holder, bic: get("bic")?.toUpperCase() ?? null, bank_name: get("bank_name") };
    }
    const key = name.toLowerCase();
    const duplicate = email && seenMail.has(email) ? t("gleiche E-Mail", "same email") : key && seenName.has(key) ? t("gleicher Name", "same name") : null;
    if (email) seenMail.add(email);
    if (key) seenName.add(key);
    return {
      line: i + 2, errors, duplicate, label: name || email || t(`Zeile ${i + 2}`, `Row ${i + 2}`), notes,
      extra: payout || sbDay ? { ...(payout ? { payout } : {}), ...(sbDay ? { selfBillingAgreedOn: sbDay } : {}) } : undefined,
      values: errors.length ? null : {
        first_name: first!, last_name: last, display_name: get("display_name"), nickname: null, email,
        instagram_handle: ig, tiktok_handle: tt, youtube_handle: yt, whatsapp_phone: phone && PHONE_RULE.test(phone) ? phone : null,
        street: get("street"), postal_code: get("postal_code"), city: get("city"), country, vat_id: get("vat_id"), tax_number: get("tax_number"),
        small_business: small ?? false, commission_bp: bp ?? null, company_name: get("company_name"), language,
        legal_form: get("legal_form"), birth_date: birth ?? null, guardian_name: get("guardian_name"), guardian_email: guardianEmail,
      },
    };
  });
  return { map, rows: out };
}

export function brandRows(rows: readonly string[][], existing: readonly Pick<BrandFull, "name" | "legal_name" | "vat_id">[]): { map: Record<string, number>; rows: ImportRow<BrandPatch>[] } {
  const [header = [], ...body] = rows;
  const map = columnMap("marken", header);
  const norm = (v: string | null | undefined) => (v ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  const seenName = new Set(existing.map((b) => b.name.trim().toLowerCase()));
  const seenVat = new Set(existing.map((b) => norm(b.vat_id)).filter(Boolean));
  const out = body.map((r, i): ImportRow<BrandPatch> => {
    const get = (k: string) => (map[k] === undefined ? null : cleanText(r[map[k]!]));
    const errors: string[] = [];
    const name = get("name") ?? get("legal_name");
    if (!name) errors.push(t("Name fehlt", "Name missing"));
    const email = get("invoice_email")?.toLowerCase() ?? null;
    if (email && !emailValid(email)) errors.push(t("Rechnungs-E-Mail ungültig", "Invoice email invalid"));
    const termsRaw = get("payment_terms_days");
    const terms = termsRaw === null ? null : Number(termsRaw.replace(/\D+$/, ""));
    if (terms !== null && (!Number.isInteger(terms) || terms < 0 || terms > 180)) errors.push(t("Zahlungsziel 0 bis 180 Tage", "Payment term 0 to 180 days"));
    const country = countryOf(get("country"), errors);
    const vat = get("vat_id");
    const language = languageOf(get("language"), errors);
    const route = routeOf(get("invoice_route"), errors);
    // Ansprechpartner mit Rolle (je Zeile einer; weitere ergänzt man an der Marke)
    let contact: ImportExtra["contact"];
    const cName = get("contact_name"), cMail = get("contact_email")?.toLowerCase() ?? null, cPhone = get("contact_phone");
    const roles = rolesOf(get("contact_role"), errors);
    if (cMail && !emailValid(cMail)) errors.push(t("Kontakt-E-Mail ungültig", "Contact email invalid"));
    if (cName || cMail) {
      if (!cName) errors.push(t("Ansprechpartner: Name fehlt", "Contact person: name missing"));
      if (roles.some((r) => r !== "briefing") && !cMail) errors.push(t("Ansprechpartner mit Rolle Rechnung, Vertrag oder Insights braucht eine E-Mail", "A contact person with the role invoice, contract or insights needs an email"));
      contact = { name: cName ?? "", email: cMail, phone: cleanPhone(cPhone), role: null, is_invoice_recipient: roles.includes("invoice"), roles };
    } else if (roles.length) errors.push(t("Rolle ohne Ansprechpartner", "Role without contact person"));
    const duplicate = name && seenName.has(name.trim().toLowerCase()) ? t("gleicher Name", "same name") : vat && seenVat.has(norm(vat)) ? t("gleiche USt-IdNr.", "same VAT ID") : null;
    if (name) seenName.add(name.trim().toLowerCase());
    if (vat) seenVat.add(norm(vat));
    return {
      line: i + 2, errors, duplicate, label: name ?? t(`Zeile ${i + 2}`, `Row ${i + 2}`), extra: contact ? { contact } : undefined,
      values: errors.length ? null : {
        name: name!, kind: "brand", legal_name: get("legal_name"), street: get("street"), postal_code: get("postal_code"), city: get("city"), country,
        invoice_email: email, vat_id: vat, register_info: get("register_info"), payment_terms_days: terms, default_currency: null,
        language: language ?? (country ? (["DE", "AT", "CH", "LI"].includes(country) ? "de" : "en") : null), is_business: true, represented_by: null,
        invoice_via_agency: false, invoice_route: route,
      },
    };
  });
  return { map, rows: out };
}

// ── Lesbarer Export im Importformat (Praxisfall „Agentur kündigt und nimmt Daten mit“) ──────────────

// Formelschutz wie alle Exporte (GB-W4); parseCsv nimmt das Schutz-Apostroph beim Einlesen wieder weg (Rundlauf)
const csvCell = (v: unknown): string => {
  const s = csvCellGuard(v === null || v === undefined ? "" : typeof v === "boolean" ? (v ? "ja" : "nein") : String(v));
  return /[;"\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const csvOf = (header: readonly string[], rows: readonly (readonly unknown[])[]) =>
  "\uFEFF" + [header.join(";"), ...rows.map((r) => r.map(csvCell).join(";"))].join("\r\n") + "\r\n";

type Rec = Record<string, unknown>;
const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));

/** Creator als CSV in genau den Spalten, die der Import erkennt (mit Bankdaten, soweit vorhanden). */
export function creatorExportCsv(talents: readonly Rec[], payouts: readonly Rec[] = []): string {
  const bank = new Map(payouts.map((p) => [str(p.talent_id), p]));
  const header = [t("Vorname", "First name"), t("Nachname", "Last name"), t("Anzeigename", "Display name"), t("E-Mail", "Email"), "Instagram", "TikTok", "YouTube", t("Telefon", "Phone"), t("Straße", "Street"), "PLZ", t("Ort", "City"), t("Land", "Country"),
    t("Steuernummer", "Tax number"), t("USt-IdNr.", "VAT ID"), t("Kleinunternehmer", "Small business"), t("Firma", "Company"), t("Sprache", "Language"), t("Provision", "Commission"), "IBAN", t("Kontoinhaber", "Account holder"), "BIC", "Bank"];
  const rows = [...talents].filter((t) => t.status !== "archived").sort((a, b) => str(a.first_name).localeCompare(str(b.first_name), "de")).map((t) => {
    const p = bank.get(str(t.id));
    return [t.first_name, t.last_name, t.display_name, t.email, t.instagram_handle, t.tiktok_handle, t.youtube_handle, t.whatsapp_phone, t.street, t.postal_code,
      t.city, t.country, t.tax_number, t.vat_id, !!t.small_business, t.company_name, t.language,
      t.commission_bp === null || t.commission_bp === undefined ? "" : String(Number(t.commission_bp) / 100).replace(".", ","),
      p?.iban, p?.bank_account_holder, p?.bic, p?.bank_name];
  });
  return csvOf(header, rows);
}

/** Marken als CSV im Importformat; je Marke der Rechnungsempfänger (sonst der erste Kontakt) als Ansprechpartner. */
export function brandExportCsv(brands: readonly Rec[], contacts: readonly Rec[] = []): string {
  const label = (roles: unknown, invoice: unknown) => {
    const r = Array.isArray(roles) ? (roles as string[]) : [];
    const all = invoice && !r.includes("invoice") ? ["invoice", ...r] : r;
    return all.map((k) => CONTACT_ROLES.find((x) => x.key === k)?.label ?? k).join(", ");
  };
  const header = ["Name", t("Rechtlicher Name", "Legal name"), t("Straße", "Street"), "PLZ", t("Ort", "City"), t("Land", "Country"), t("USt-IdNr.", "VAT ID"), t("Rechnungs-E-Mail", "Invoice email"), t("Zahlungsziel", "Payment term"), "Register", t("Sprache", "Language"),
    t("Abrechnungsweg", "Invoicing route"), t("Ansprechpartner", "Contact person"), t("Kontakt-E-Mail", "Contact email"), t("Kontakt-Telefon", "Contact phone"), t("Rolle", "Role")];
  const rows = [...brands].filter((b) => b.kind !== "agency").sort((a, b) => str(a.name).localeCompare(str(b.name), "de")).map((b) => {
    const own = contacts.filter((c) => c.brand_id === b.id);
    const c = own.find((x) => x.is_invoice_recipient) ?? own[0];
    return [b.name, b.legal_name, b.street, b.postal_code, b.city, b.country, b.vat_id, b.invoice_email, b.payment_terms_days, b.register_info, b.language,
      b.invoice_route === "via_agency" ? t("über die Agentur", "via the agency") : b.invoice_route === "direct_to_brand" ? t("direkt an die Marke", "directly to the brand") : "",
      c?.name, c?.email, c?.phone, c ? label(c.roles, c.is_invoice_recipient) : ""];
  });
  return csvOf(header, rows);
}

/** Alle Ansprechpartner der Marken (eine Zeile je Kontakt). */
export function contactsExportCsv(brands: readonly Rec[], contacts: readonly Rec[]): string {
  const names = new Map(brands.map((b) => [str(b.id), str(b.name)]));
  const header = [t("Marke", "Brand"), t("Ansprechpartner", "Contact person"), t("Kontakt-E-Mail", "Contact email"), t("Kontakt-Telefon", "Contact phone"), t("Funktion", "Job title"), t("Rolle", "Role")];
  const rows = [...contacts].sort((a, b) => (names.get(str(a.brand_id)) ?? "").localeCompare(names.get(str(b.brand_id)) ?? "", "de")).map((c) => {
    const r = Array.isArray(c.roles) ? (c.roles as string[]) : [];
    const all = c.is_invoice_recipient && !r.includes("invoice") ? ["invoice", ...r] : r;
    return [names.get(str(c.brand_id)), c.name, c.email, c.phone, c.role, all.map((k) => CONTACT_ROLES.find((x) => x.key === k)?.label ?? k).join(", ")];
  });
  return csvOf(header, rows);
}
